import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Video, Youtube, Filter } from 'lucide-react';
import { blogPosts } from '../data/mock';
import SEO from '../components/SEO';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const categories = [
    { id: 'all', name: 'All Posts', color: '#1e3a8a' },
    { id: 'cire', name: 'CIRE Exam', color: '#1e40af' },
    { id: 'rse', name: 'RSE Exam', color: '#1d4ed8' },
    { id: 'ise', name: 'ISE Exam', color: '#2563eb' },
    { id: 'derivatives', name: 'Derivatives', color: '#3b82f6' },
    { id: 'career', name: 'Career & Industry', color: '#d4af37' },
    { id: 'study-tips', name: 'Study Tips', color: '#059669' }
  ];
  
  const filteredPosts = useMemo(() => {
    if (selectedCategory === 'all') return blogPosts;
    return blogPosts.filter(post => post.category === selectedCategory);
  }, [selectedCategory]);
  
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return blogPosts.length;
    return blogPosts.filter(post => post.category === categoryId).length;
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRO Exam Blog 2025 - Study Tips, Exam Strategies & Industry Updates"
        description="Expert CIRO exam tips, study strategies, and regulatory updates. Learn how to pass CIRE, RSE, ISE, and Derivatives exams. CIRO proficiency model 2026 changes, exam format guides, study plans, common mistakes to avoid. Written by certified instructors with 40+ years experience."
        keywords="CIRO exam tips, CIRE exam tips, how to pass CIRE exam, how to register for CIRO exam, how to book CIRO exam date, CIRO exam dates, CIRO exam scheduling, CIRO exam format, CIRO exam outline, CIRO passing score, CIRO rewrite policy, CIRO retake policy, CIRO common mistakes, CIRO study plan, CIRO 30 day study plan, CIRO 60 day study plan, CIRO 90 day study plan, RSE vs ISE, which CIRO exam do I need, CIRO proficiency requirements registered representative, CIRO proficiency requirements supervisor, CIRO proficiency requirements CCO, CIRO proficiency requirements CFO, IIROC proficiency requirements 2026, MFDA proficiency requirements 2026"
        ogTitle="CIRO Exam Blog - Expert Tips, Strategies & Regulatory Updates"
        ogDescription="Expert exam strategies and study tips for CIRO certifications. Written by industry professionals with 40+ years of combined experience."
      />
      {/* Header Section */}
      <section style={{
        padding: '120px 0 80px',
        backgroundColor: 'var(--bg-card)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="brand-display" style={{ marginBottom: '24px' }}>
            Blog & Resources
          </h1>
          <p className="body-large" style={{ 
            maxWidth: '800px',
            margin: '0 auto',
            color: 'var(--neutral-light)'
          }}>
            Expert insights, exam strategies, and updates on CIRO certifications
          </p>
        </div>
      </section>

      {/* Featured Video Section */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '50px',
            alignItems: 'center'
          }}>
            {/* Video Embed */}
            <div style={{
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
              aspectRatio: '16/9'
            }}>
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/2ymEjmaLq60"
                title="How to Become a Financial Advisor in Canada"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  border: 'none',
                  display: 'block'
                }}
              ></iframe>
            </div>

            {/* Video Content */}
            <div>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: 'rgba(212, 175, 55, 0.2)',
                border: '1px solid var(--gold-primary)',
                borderRadius: '20px',
                fontSize: '0.875rem',
                color: 'var(--gold-primary)',
                marginBottom: '20px',
                fontWeight: 600
              }}>
                <Youtube size={16} /> Featured Video
              </span>
              <h2 style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                fontWeight: 800,
                marginBottom: '16px',
                lineHeight: 1.2
              }}>
                How to Become a Financial Advisor in Canada
              </h2>
              <p style={{
                fontSize: '1.05rem',
                lineHeight: 1.8,
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '24px'
              }}>
                Watch our comprehensive guide on starting your career in the Canadian investment industry. Learn about CIRO certification pathways, exam requirements, and career opportunities.
              </p>
              <a 
                href="https://www.youtube.com/@FinanceinCanada" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-gold"
              >
                <Video size={18} /> Subscribe on YouTube
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Study Guides Section */}
      <section style={{
        padding: '60px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 700,
              color: 'var(--brand-primary)',
              marginBottom: '12px'
            }}>
              Free Study Guides
            </h2>
            <p style={{ color: 'var(--neutral-light)', maxWidth: '600px', margin: '0 auto' }}>
              Structured learning paths to help you prepare for CIRO exams
            </p>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            <Link 
              to="/cire-study-guide"
              data-testid="cire-study-guide-link"
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '12px',
                textDecoration: 'none',
                display: 'block',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(30, 58, 138, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '6px' }}>
                CIRE Study Guide
              </h3>
              <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '12px' }}>
                6-phase learning path • 4-6 weeks
              </p>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#d4af37'
              }}>
                Start Learning <ArrowRight size={16} />
              </span>
            </Link>
            
            <Link 
              to="/rse-study-guide"
              data-testid="rse-study-guide-link"
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '12px',
                textDecoration: 'none',
                display: 'block',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(30, 58, 138, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '6px' }}>
                RSE Study Guide
              </h3>
              <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '12px' }}>
                11-phase learning path • 6-8 weeks
              </p>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#d4af37'
              }}>
                Start Learning <ArrowRight size={16} />
              </span>
            </Link>
            
            <Link 
              to="/ise-study-guide"
              data-testid="ise-study-guide-link"
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '12px',
                textDecoration: 'none',
                display: 'block',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(30, 58, 138, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '6px' }}>
                ISE Study Guide
              </h3>
              <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '12px' }}>
                11-phase learning path • 8-10 weeks
              </p>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#d4af37'
              }}>
                Start Learning <ArrowRight size={16} />
              </span>
            </Link>
            
            <Link 
              to="/derivatives-study-guide"
              data-testid="derivatives-study-guide-link"
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                color: 'white',
                padding: '25px',
                borderRadius: '12px',
                textDecoration: 'none',
                display: 'block',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(30, 58, 138, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <h3 style={{ fontSize: '1.15rem', fontWeight: '700', marginBottom: '6px' }}>
                Derivatives Study Guide
              </h3>
              <p style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '12px' }}>
                9-phase learning path • 8-10 weeks
              </p>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#d4af37'
              }}>
                Start Learning <ArrowRight size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter Section */}
      <section style={{
        padding: '60px 0 40px',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h2 style={{ 
              fontSize: '1.75rem', 
              fontWeight: '700', 
              color: '#1e3a8a',
              marginBottom: '10px'
            }}>
              Browse by Topic
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
              Filter {blogPosts.length} articles by exam type or topic
            </p>
          </div>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'center'
          }}>
            {categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`filter-${cat.id}`}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: '12px 20px',
                  borderRadius: '30px',
                  border: selectedCategory === cat.id ? 'none' : '2px solid #e5e7eb',
                  background: selectedCategory === cat.id 
                    ? `linear-gradient(135deg, ${cat.color} 0%, ${cat.color}dd 100%)`
                    : 'white',
                  color: selectedCategory === cat.id ? 'white' : '#374151',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: selectedCategory === cat.id 
                    ? '0 4px 12px rgba(30, 58, 138, 0.3)' 
                    : 'none'
                }}
                onMouseOver={(e) => {
                  if (selectedCategory !== cat.id) {
                    e.currentTarget.style.borderColor = cat.color;
                    e.currentTarget.style.color = cat.color;
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedCategory !== cat.id) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.color = '#374151';
                  }
                }}
              >
                {cat.name}
                <span style={{
                  background: selectedCategory === cat.id 
                    ? 'rgba(255,255,255,0.25)' 
                    : '#f3f4f6',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.8rem',
                  fontWeight: '700'
                }}>
                  {getCategoryCount(cat.id)}
                </span>
              </button>
            ))}
          </div>
          
          {selectedCategory !== 'all' && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px' 
            }}>
              <button
                onClick={() => setSelectedCategory('all')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6b7280',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Blog Posts Section */}
      <section style={{
        padding: '40px 0 96px',
        backgroundColor: 'var(--bg-page)'
      }}>
        <div className="container">
          {/* Results count */}
          <div style={{ 
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '0.95rem',
              margin: 0
            }}>
              Showing <strong style={{ color: '#1e3a8a' }}>{filteredPosts.length}</strong> articles
              {selectedCategory !== 'all' && (
                <span> in <strong style={{ color: '#1e3a8a' }}>
                  {categories.find(c => c.id === selectedCategory)?.name}
                </strong></span>
              )}
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {filteredPosts.map((post) => (
              <article key={post.id} style={{
                backgroundColor: 'var(--bg-card)',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, border-color 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }} className="card-hover-effect">
                {/* Blog Post Image */}
                <div style={{
                  width: '100%',
                  height: '240px',
                  backgroundColor: 'var(--secondary-olive)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {post.image ? (
                    <img 
                      src={post.image} 
                      alt={post.title}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <span style={{ 
                      color: 'var(--text-muted)',
                      fontSize: '0.875rem'
                    }}>
                      Blog Post Image
                    </span>
                  )}
                </div>
                
                <div style={{
                  padding: '32px',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    <Calendar size={16} color="var(--brand-primary)" />
                    <span className="caption">{post.date}</span>
                  </div>
                  
                  <h3 className="heading-5" style={{ 
                    marginBottom: '16px',
                    flex: 1
                  }}>
                    {post.title}
                  </h3>
                  
                  <p className="body-small" style={{ marginBottom: '24px' }}>
                    {post.excerpt}
                  </p>
                  
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '24px'
                  }}>
                    {post.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="caption" style={{
                        padding: '6px 12px',
                        backgroundColor: 'var(--secondary-olive)',
                        borderRadius: '4px'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <Link to={`/blog/${post.slug}`} className="btn-secondary" style={{ width: '100%' }}>
                    Read More <ArrowRight size={18} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Topics Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--bg-card)'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Featured Topics
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px'
          }}>
            {[
              'CIRE Exam Preparation',
              'RSE Study Strategies',
              'CIRO Regulatory Updates',
              'Investment Advisor Career',
              'Exam Tips & Tricks',
              'Success Stories'
            ].map((topic, index) => (
              <div key={index} style={{
                padding: '32px',
                backgroundColor: 'var(--bg-page)',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                textAlign: 'center',
                transition: 'transform 0.3s ease, border-color 0.3s ease',
                cursor: 'pointer'
              }} className="card-hover-effect">
                <h4 className="heading-5">{topic}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 0',
        backgroundColor: 'var(--secondary-olive)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px' }}>
            Stay Updated
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '40px',
            maxWidth: '700px',
            margin: '0 auto 40px'
          }}>
            Get the latest exam tips, study strategies, and CIRO updates delivered to your inbox
          </p>
          <Link to="/contact" className="btn-primary">
            Contact Us <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;